'use client';

import { c as _c } from "react/compiler-runtime";
import { Button } from '@algoacademy/design-system/web/actions/Button';
import { Link } from '@algoacademy/design-system/web/actions/Link';
import { showErrorToastWithAlgoPhoneNumber } from '@algoacademy/design-system/web/feedback/Toaster/ToasterError';
import { Checkbox, CheckboxField } from '@algoacademy/design-system/web/forms/Checkbox';
import { ErrorMessage, Field, Label } from '@algoacademy/design-system/web/forms/Fieldset';
import { Input } from '@algoacademy/design-system/web/forms/Input';
import { Textarea } from '@algoacademy/design-system/web/forms/Textarea';
import { VerticalSpacer } from '@algoacademy/design-system/web/layouts/VerticalSpacer';
import { H3 } from '@algoacademy/design-system/web/marketing/typography/Heading';
import { P3 } from '@algoacademy/design-system/web/marketing/typography/Paragraph';
import { Dialog, DialogBody } from '@algoacademy/design-system/web/overlays/Dialog';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useId, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { useTRPC } from '@/app/_trpc/config';
import { type ScholarshipApplicationFormData, scholarshipApplicationDefaultValues, scholarshipApplicationSchemaZ } from './schema';
function ScholarshipApplicationFormFields({
  close,
  openKey
}: {
  close: () => void;
  openKey: number;
}) {
  const trpc = useTRPC();
  const submitMutation = useMutation(trpc.notifications.submitScholarshipApplication.mutationOptions());
  const currentOpenKey = openKey;
  const methods = useForm<ScholarshipApplicationFormData>({
    mode: 'onSubmit',
    resolver: zodResolver(scholarshipApplicationSchemaZ),
    defaultValues: scholarshipApplicationDefaultValues
  });
  const {
    register,
    control,
    handleSubmit,
    formState: {
      errors,
      isSubmitting
    }
  } = methods;
  const onSubmit = async (data: ScholarshipApplicationFormData) => {
    try {
      await submitMutation.mutateAsync({
        email: data.email,
        fullName: data.fullName,
        phone: data.phone.trim() || null,
        message: data.message.trim() || null,
        consentContact: true,
        consentMarketingOffers: data.consentMarketingOffers
      });
      if (currentOpenKey !== openKey) return;
      toast.success('Wniosek został przesłany', {
        description: 'Dziękujemy — odezwiemy się w ciągu dwóch tygodni.'
      });
      close();
    } catch {
      if (currentOpenKey === openKey) {
        showErrorToastWithAlgoPhoneNumber();
      }
    }
  };
  return <form onSubmit={handleSubmit(onSubmit)} className='flex min-h-0 min-w-0 flex-1 flex-col pt-4'>
      <DialogBody className='mt-0 flex min-h-0 min-w-0 flex-1 flex-col space-y-4 overflow-y-auto px-2'>
        <Field>
          <Label>Imię i nazwisko*</Label>
          <Input {...register('fullName')} type='text' autoComplete='name' />
          <ErrorMessage>{errors.fullName?.message}</ErrorMessage>
        </Field>

        <Field>
          <Label>Adres e-mail*</Label>
          <Input {...register('email')} type='email' autoComplete='email' />
          <ErrorMessage>{errors.email?.message}</ErrorMessage>
        </Field>

        <Field>
          <Label>Telefon (opcjonalnie)</Label>
          <Input {...register('phone')} type='tel' autoComplete='tel' />
          <ErrorMessage>{errors.phone?.message}</ErrorMessage>
        </Field>

        <Field>
          <Label>Opowiedz o sobie i swoich celach (opcjonalnie)</Label>
          <Textarea {...register('message')} rows={5} />
          <ErrorMessage>{errors.message?.message}</ErrorMessage>
        </Field>

        <div className='border-borderPrimary border-t pt-4'>
          <P3 className='font-medium text-contentPrimary'>Zgody prawne</P3>
          <VerticalSpacer size='paragraph' />

          <div className='space-y-3'>
            <Field>
              <Controller name='consentContact' control={control} render={({
              field: {
                value,
                onChange
              }
            }) => <CheckboxField className='items-start'>
                    <Checkbox checked={value} onChange={onChange} />
                    <Label className='text-sm leading-snug lg:text-[0.9375rem] lg:leading-snug'>
                      Wyrażam zgodę na kontakt mailowy (i telefoniczny, jeśli podałem numer) w
                      sprawie niniejszego wniosku o stypendium. Podstawy i zakres przetwarzania
                      danych znajdują się w{' '}
                      <Link href='/polityka-prywatnosci' className='font-semibold text-algoprimary' rel='noopener noreferrer' target='_blank'>
                        Polityce prywatności
                      </Link>
                      .<span className='text-contentRed'>*</span>
                    </Label>
                  </CheckboxField>} />
              <ErrorMessage>{errors.consentContact?.message}</ErrorMessage>
            </Field>

            <Field>
              <Controller name='consentMarketingOffers' control={control} render={({
              field: {
                value: value_0,
                onChange: onChange_0
              }
            }) => <CheckboxField className='items-start'>
                    <Checkbox checked={value_0} onChange={onChange_0} />
                    <Label className='text-contentSecondary text-sm leading-snug lg:text-[0.9375rem] lg:leading-snug'>
                      Wyrażam dobrowolną zgodę na otrzymywanie od AlgoAcademy informacji
                      marketingowych drogą elektroniczną. Zgodę mogę w każdej chwili wycofać.
                    </Label>
                  </CheckboxField>} />
            </Field>
          </div>
        </div>

        <div className='flex flex-col-reverse gap-2 sm:flex-row sm:justify-end'>
          <Button outline type='button' onClick={close}>
            Zamknij
          </Button>
          <Button type='submit' loading={isSubmitting || submitMutation.isPending}>
            Wyślij wniosek
          </Button>
        </div>
      </DialogBody>
    </form>;
}
export function ScholarshipApplicationDialog(t0) {
  const $ = _c(21);
  const {
    open,
    onClose
  } = t0;
  const [openKey, setOpenKey] = useState(0);
  const titleId = useId();
  let t1;
  let t2;
  if ($[0] !== open) {
    t1 = () => {
      if (open) {
        setOpenKey(_temp3);
      }
    };
    t2 = [open];
    $[0] = open;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] !== titleId) {
    t3 = <H3 id={titleId} className="min-w-0 flex-1 text-balance pr-2">Wniosek o stypendium AlgoAcademy</H3>;
    $[3] = titleId;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <XMarkIcon className="size-5 text-contentSecondary" />;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== onClose) {
    t5 = <Button plain={true} onClick={onClose} aria-label="Zamknij" className="shrink-0">{t4}</Button>;
    $[6] = onClose;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t3 || $[9] !== t5) {
    t6 = <div className="flex shrink-0 flex-row items-start justify-between gap-4 px-2">{t3}{t5}</div>;
    $[8] = t3;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== onClose || $[12] !== open || $[13] !== openKey) {
    t7 = open && <ScholarshipApplicationFormFields key={openKey} openKey={openKey} close={onClose} />;
    $[11] = onClose;
    $[12] = open;
    $[13] = openKey;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== onClose || $[16] !== open || $[17] !== t6 || $[18] !== t7 || $[19] !== titleId) {
    t8 = <Dialog open={open} onClose={onClose} type="default" className="flex max-h-[min(92vh,44rem)] max-w-xl flex-col" aria-labelledby={titleId}>{t6}{t7}</Dialog>;
    $[15] = onClose;
    $[16] = open;
    $[17] = t6;
    $[18] = t7;
    $[19] = titleId;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  return t8;
}
function _temp3(key) {
  return key + 1;
}
export function ScholarshipApplyButton(t0) {
  const $ = _c(11);
  const {
    className,
    size,
    children
  } = t0;
  const [open, setOpen] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => setOpen(true);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== children || $[2] !== className || $[3] !== size) {
    t2 = <Button className={className} size={size} onClick={t1}>{children}</Button>;
    $[1] = children;
    $[2] = className;
    $[3] = size;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => setOpen(false);
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== open) {
    t4 = <ScholarshipApplicationDialog open={open} onClose={t3} />;
    $[6] = open;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t2 || $[9] !== t4) {
    t5 = <>{t2}{t4}</>;
    $[8] = t2;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
}